/**
 * VAL-PATH-ADJUST P4b — the four legs on step 3 (Adjustments and rates),
 * moved here from Intake's deleted `_IntakeLegs.tsx`. Labels carry no
 * asterisk; each field's shipped hint now sits behind a help dot outside the
 * label, except the crossover hint, which stays visible. Below two years of
 * financials the inputs are locked with the owner-approved message and a link
 * back to Financials.
 */
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { InfoDot } from '@/components/ui/info-dot'
import type { ValuationMethodology } from '@/types/valuation'
import { basisLabel, money } from './_shared'
import { LOCKED_MESSAGE } from './_steps'

export function Field({
  id, label, value, onChange, placeholder, hint, help, disabled, type = 'number',
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
  /** Help-dot body; the dot sits beside the label, never inside it. */
  help?: string
  disabled?: boolean
  type?: string
}) {
  return (
    <div>
      <div className="flex items-center gap-1">
        <Label htmlFor={id} className="text-xs">{label}</Label>
        {help && <InfoDot label={label} data-testid={`info-${id}`}>{help}</InfoDot>}
      </div>
      <Input
        id={id}
        type={type}
        step="any"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  )
}

export interface LegValues {
  replacementCompensation: string
  baseCashFlow: string
  compMultiples: string
  fairValueAdjustments: string
}

export function AdjustLegsSection({
  engagementId, methodology, values, onChange, locked,
}: {
  engagementId: string
  methodology?: ValuationMethodology | null
  values: LegValues
  onChange: (key: keyof LegValues, value: string) => void
  locked: boolean
}) {
  const rule = methodology?.earnings_basis_rule
  const crossoverHint = rule
    ? `Signed SDE↔EBITDA crossover is ${money(rule.crossover_earnings_threshold)} — below it the basis is ${basisLabel(String(rule.basis_below_threshold))}, at or above it ${basisLabel(String(rule.basis_at_or_above_threshold))}.`
    : undefined
  const set = (key: keyof LegValues) => (v: string) => onChange(key, v)

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        {locked && (
          <p className="text-sm text-amber-700">
            {LOCKED_MESSAGE}{' '}
            <Link to={`/valuation/${engagementId}/financials`} className="underline">Go to Financials</Link>
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="replacement_compensation" label="Replacement owner compensation"
            value={values.replacementCompensation} onChange={set('replacementCompensation')}
            placeholder="120000" disabled={locked} hint={crossoverHint}
            help="What it would cost to hire someone to do the owner's job, per year. Deducted before the income approach capitalizes earnings."
          />
          <Field
            id="base_cash_flow" label="Base cash flow (DCF)"
            value={values.baseCashFlow} onChange={set('baseCashFlow')}
            disabled={locked} help="Blank uses normalized earnings."
          />
          <Field
            id="comp_multiples" label="Comparable multiples" type="text"
            value={values.compMultiples} onChange={set('compMultiples')}
            placeholder="3.1, 3.6, 4.0" disabled={locked}
            help="Comma-separated, and optional. Left blank, the signed industry profile supplies the market leg from its own multiple band — which is the normal path. Entered comps are shown as evidence beside the signed range; they never change the value — use Override with a reason to change the multiple."
          />
          <Field
            id="fair_value_adjustments" label="Fair-value adjustments"
            value={values.fairValueAdjustments} onChange={set('fairValueAdjustments')}
            disabled={locked}
            help="Optional. Supplied together, these clamp the low end of the band; omitted, the floor leg is simply not applied."
          />
        </div>
      </CardContent>
    </Card>
  )
}
